import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button, InputCheckbox, LoadingSpinner, InlineAlert } from '../components/BaseComponents'
import OrganisationJurisdictionSelect from '../components/BaseComponents/OrganisationJurisdictionSelect'
import { defaultStyles } from '../components/BaseComponents/Forms/SearchableSelect'
import { FormEvent, useEffect, useState } from 'react';
import usePublicApi from '../hooks/usePublicApi';
import { FORM_INPUT_TEXT_CLASS, FORM_LABEL_CLASS } from '../lib/formStyles';
import { FOCUS_VISIBLE_OUTLINE_INSET, INPUT_FOCUS_RING } from '../lib/a11yFocus';
import { getApiErrorMessage, getCSRFToken, validateEmail } from '../lib/utils';
import { getNewPasswordError, PASSWORD_HINT } from '../lib/passwordPolicy';
import routes from '../components/Router/routes';

const noop = () => undefined

const DISABLED_FIELD_COLOUR = '#374654'

const disabledSelectStyles = {
    placeholder: (base: any) => ({ ...base, color: DISABLED_FIELD_COLOUR }),
    indicatorsContainer: (base: any) => {
        const styled = defaultStyles.indicatorsContainer(base)
        return { ...styled, '&::before': { ...styled['&::before'], backgroundColor: DISABLED_FIELD_COLOUR } }
    },
}

function SignUp() {
    const [termsConditions, setTermsConditions] = useState('');
    const [loading, setLoading] = useState(true);

    const publicApi = usePublicApi();
    const navigate = useNavigate();
    const location = useLocation();

    // Sub-task I item A.4 (docs/todo/user-org-membership-i-qa-actions.md): SignUp.tsx never reads
    // an inviteToken query param (unlike InviteSignUp.tsx's path param), so a stray one left over
    // from a redirect is just visual clutter - strip it cosmetically, no functional effect.
    useEffect(() => {
        if (new URLSearchParams(location.search).has('inviteToken')) {
            navigate(location.pathname, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const run = async () => {
            try {
                const result = await publicApi.get('user/loginTermsConditions');
                setTermsConditions(result.data.termsConditions)
            } catch (err) {
                console.error('Error loading signup data:', err);
                setError('Failed to load signup information. Please refresh and try again.');
            } finally {
                setLoading(false);
            }
        }
        run();
		// eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [submittedEmail, setSubmittedEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);

    const onFormSubmit = (e: FormEvent) => {
       e.preventDefault();
       onSubmit();
       return false; 
    }

    const onSubmit = async () => {
        if(!name) {
            setError('Please enter your full name');
            return;
        } else if(!validateEmail(email)) {
            setError('Please enter a valid email address')
            return;
        } else if(getNewPasswordError(password, confirmPassword, { name, email })) {
            setError(getNewPasswordError(password, confirmPassword, { name, email }) ?? '')
            return;
        } else if(!termsAccepted) {
            setError('You must confirm you are authorised to accept the Terms of Use to sign up.');
            return;
        }

        setSubmitting(true)
        setError('');

        try {
            await publicApi.post('/user/signup', {
                name,
                email,
                password,
                termsConditions,
                _csrf: await getCSRFToken(),
            });
            setSubmittedEmail(email);

        } catch(error: any) {
            setSubmitting(false);
            console.warn('Signup error:', error);
            setError(getApiErrorMessage(error, 'Failed to create your account. Please try again.'))
        }
    }

    if(loading) {
        return <LoadingSpinner />
    }

    return (
        <div className="content w-full max-w-[32rem] px-4 sm:px-0 mx-auto mt-16 mb-8">
            <div className="text-charcoal-100 text-2xl text-center">Welcome</div>
            <div className="flex text-center mt-8 text-sm">
                <Link className={`w-1/2 border-algae-200 p-2 border-b-2 text-white ${FOCUS_VISIBLE_OUTLINE_INSET}`} to={routes.publicSignUp.path}>Create an account</Link>
                <Link className={`w-1/2 text-charcoal-100 p-2 hover:text-white ${FOCUS_VISIBLE_OUTLINE_INSET}`} to={routes.publicLogin.path}>Log in</Link>
            </div>
            { submittedEmail ? (
                <InlineAlert type='success' className='mt-8'>We have sent a verification link to {submittedEmail}. Check your email and click the link to verify your address and continue setting up your account.</InlineAlert>
            ) : (
            <form onSubmit={onFormSubmit}>
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Your name</span>
                    <br />
                    <input
                        id="nameField"
                        disabled={submitting}
                        name="name"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                        type="text"
                        placeholder="e.g. Jo Bloggs"
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                </label>
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Email address</span>
                    <br />
                    <input
                        id="emailField"
                        disabled={submitting}
                        name="email"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                        type="text"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                    />
                </label>
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Organisation name (coming soon)</span>
                    <br />
                    <input
                        disabled
                        name="companyName"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING} bg-charcoal-300 cursor-default placeholder:!text-charcoal-500 disabled:[-webkit-text-fill-color:theme(colors.charcoal.500)]`}
                        type="text"
                        placeholder="Organisation name"
                        value=""
                        readOnly
                    />
                </label>
                <OrganisationJurisdictionSelect
                    value={undefined}
                    onChange={noop}
                    label="Organisation jurisdiction (coming soon)"
                    styles={disabledSelectStyles}
                    fillContainer
                    isDisabled
                />
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Password</span>
                    <br />
                    <input
                        id="passwordField"
                        disabled={submitting}
                        name="password"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                        type="password"
                        autoComplete="new-password"
                        placeholder="Create a password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                    />
                    <span className='block mt-1 text-xs text-charcoal-100'>{PASSWORD_HINT}</span>
                </label>
                <label className="block mt-8 text-sm">
                    <span className={FORM_LABEL_CLASS}>Confirm password</span>
                    <br />
                    <input
                        id="confirmPasswordField"
                        disabled={submitting}
                        name="confirmPassword"
                        className={`rounded w-full mt-1 ${FORM_INPUT_TEXT_CLASS} ${INPUT_FOCUS_RING}`}
                        type="password"
                        autoComplete="new-password"
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                    />
                </label>

                <div
                    className="mt-8 max-h-[50vh] sm:max-h-96 overflow-y-scroll rounded font-sm"
                    role="region"
                    aria-label="Neoni® Beta 2 Terms of Use"
                    tabIndex={0}
                >
                    <div className="whitespace-pre-line text-charcoal-100">{ termsConditions }</div>
                </div>
                <label className="text-white mt-6 flex items-center gap-4">
                    <InputCheckbox
                        className='h-4 w-4 border-white rounded-sm text-algae-500 cursor-pointer active:ring-2 active:ring-transparent'
                        checked={termsAccepted}
                        onChange={e => setTermsAccepted(e.target.checked)}
                    />
                    <span>I am authorised to accept the Neoni® Beta 2 Terms of Use on behalf of my organisation.</span>
                </label>

                { error && (
                    <InlineAlert type='error' className='mt-6 whitespace-pre-line'>{ error }</InlineAlert>
                )}
                <div className='mt-6 flex justify-start'>
                    <Button
                        disabled={submitting}
                        buttonType="submit"
                        type="Primary"
                        text="Create an account"
                        context="form-field"
                    />
                </div>
            </form>
            )}
        </div>
    );
}

export default SignUp
